/**
 * useIdleHold (issue #29).
 *
 * Declarative front door to the `DashboardStore` idle-hold register: while
 * `active` is true, this component asks the wall-tablet idle timer to defer.
 * `useIdleTimer` reads the register on every fire and reschedules instead of
 * calling `goIdle()` while any hold is live.
 *
 *   useIdleHold(open);                       // BottomSheet
 *   useHoldIdleWhileAnswering(state.status); // assistant, via this hook
 *
 * Why a hook and not `useDashboard.getState().holdIdle()` inline: the classic
 * failure mode of a ref-counted registry is the leaked reference — a caller
 * acquires, then unmounts on a path that never runs its release, and the
 * counter never returns to zero.  Binding acquire to `useEffect` and release
 * to that effect's cleanup makes the leak structurally impossible: React runs
 * the cleanup on unmount and before every re-run, so acquire/release always
 * pair up.  `holdIdle()` must therefore never be called directly from a
 * component — always go through this hook.
 *
 * A hold is a request, not a veto.  `useIdleTimer` still has a five-minute
 * deadman ceiling that fades the screen regardless; see its docblock.
 *
 * Lives in `lib/`, not `features/dashboard/`, because its callers cut across
 * layers: `components/primitives/BottomSheet` is a primitive, and a primitive
 * must not import from a feature — that would bind all ~18 sheet call sites to
 * `features/dashboard`.  `lib/useViewport` sets the same precedent for a
 * shared hook.  The hook still reads `DashboardStore`; only its own location
 * is neutral.
 */
import { useEffect } from 'react';
import { useDashboard } from '../features/dashboard/DashboardStore';

export function useIdleHold(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    // getState() rather than a subscription: this hook must not re-render its
    // owner when unrelated dashboard state (isIdle, deviceClass) changes.
    return useDashboard.getState().holdIdle();
  }, [active]);
}
